/**
 * Community API: posts, reactions, comments.
 */
import { apiGet, apiPost, apiPostFormData } from '../../../shared/api/client';
import { endpoints } from '../../../shared/config/endpoints';
import type { CommunityPost, CommunityPostsResponse } from './apiTypes';

function postRecencyMs(post: CommunityPost): number {
  const raw = post.created_at;
  if (typeof raw === 'string' && raw.trim() !== '') {
    const ms = Date.parse(raw);
    if (Number.isFinite(ms)) {
      return ms;
    }
  }
  return Number(post.id) || 0;
}

/** Newest first by `created_at`, then by `id` as tiebreaker. */
export function sortPostsNewestFirst(posts: CommunityPost[]): CommunityPost[] {
  return [...posts].sort((a, b) => {
    const by_time = postRecencyMs(b) - postRecencyMs(a);
    if (by_time !== 0) {
      return by_time;
    }
    return (Number(b.id) || 0) - (Number(a.id) || 0);
  });
}

export async function fetchPosts(): Promise<CommunityPost[]> {
  const res = await apiGet<CommunityPostsResponse>(endpoints.posts);
  const posts = res?.data?.posts;
  if (!Array.isArray(posts)) {
    return [];
  }
  return sortPostsNewestFirst(posts);
}

export async function likePost(params: { post_id: number; customer_id: number }): Promise<unknown> {
  /** Postman: reaction field is required (`like`). */
  const res = await apiPost<unknown>(endpoints.reaction, {
    post_id: params.post_id,
    customer_id: params.customer_id,
    reaction: 'like',
  });
  return res;
}

export type CreatePostPayload = {
  customer_id: number;
  content: string;
  /** Local file URI from image picker — sent as multipart when present. */
  attachment?: string;
  attachment_type?: string;
  attachment_name?: string;
};

export async function createPost(payload: CreatePostPayload): Promise<unknown> {
  const form = new FormData();
  form.append('customer_id', String(payload.customer_id));
  form.append('content', payload.content);
  if (payload.attachment) {
    form.append('attachment', {
      uri: payload.attachment,
      type: payload.attachment_type || 'image/jpeg',
      name: payload.attachment_name || 'community-post.jpg',
    } as unknown as Blob);
  }
  return apiPostFormData<unknown>(endpoints.posts, form);
}

export async function addComment(payload: { post_id: number; customer_id: number; content: string }): Promise<unknown> {
  const res = await apiPost<unknown>(endpoints.comments, payload);
  return res;
}
